import { useEffect, useMemo, useState } from 'react';
import { valueFormat } from '@intelligence/utils/commonUtils';
import { useQueryDefinition } from '@intelligence/hooks/useQueryDefinition';
import { buildShowSumAvgMetrics, IMetricsDataSourceItem } from './sumAvgUtils';

interface IUseShowSumAvgInfo {
  metric?: string;
  sumAvgMaxList?: { date: 'sum' | 'avg'; metrics: { metric: string; value: number }[] }[];
}

/**
 * 获取当前指标展示 sum、avg 的相关信息
 *
 * @param {IUseShowSumAvgInfo} param0
 * @param {string} param0.metric 当前 metric
 * @param {{}} param0.sumAvgMaxList sum、avg 数据列表
 * @returns {{ currentMetricSumAvg: {sum: string, avg: string}; showSum: boolean; showAvg: boolean; }}
 */
function useShowSumAvgInfo({ metric, sumAvgMaxList }: IUseShowSumAvgInfo) {
  const { showSum, showAvg, showMax } = useShowSumAvgState(metric);

  const currentMetricSumAvg = useMemo(() => {
    // 找到对应列表
    const sumList = sumAvgMaxList?.find((item) => item?.date === 'sum');
    const avgList = sumAvgMaxList?.find((item) => item?.date === 'avg');

    // 找到对应值
    const currentMetricsSumValue = sumList?.metrics?.find(
      (item: { metric: string }) => item?.metric === metric,
    )?.value;
    const currentMetricsAvgValue = avgList?.metrics?.find(
      (item: { metric: string }) => item?.metric === metric,
    )?.value;

    // 将值格式化
    return {
      sum: valueFormat(currentMetricsSumValue, false, 0),
      avg: valueFormat(currentMetricsAvgValue, false, 0),
    };
  }, [sumAvgMaxList, metric]);

  return { currentMetricSumAvg, showSum, showAvg, showMax };
}

type TMetricsDataSource = IMetricsDataSourceItem[];

/**
 * 判断当前指标是否需要展示 sum、avg 的状态
 *
 * @param {?string} [metric] 当前 metric 参数
 * @returns {{ showSum: boolean; showAvg: boolean; }} 返回当前指标是否展示 sum、avg 的状态
 */
const useShowSumAvgState = (metric?: string) => {
  const [showSum, setShowSum] = useState<boolean>();
  const [showAvg, setShowAvg] = useState<boolean>();
  const [showMax, setShowMax] = useState<boolean>();

  const showSumAvgMetrics = useShowSumAvgConfig();

  // 指定当前 metric 是否需要展示 sum、avg 的状态
  useEffect(() => {
    const shouldShowSum = showSumAvgMetrics?.sum?.find((item: string) => item === metric);
    const shouldShowAvg = showSumAvgMetrics?.avg?.find((item: string) => item === metric);
    const shouldShowMax = showSumAvgMetrics?.max?.find((item: string) => item === metric);

    setShowSum(Boolean(shouldShowSum));
    setShowAvg(Boolean(shouldShowAvg));
    setShowMax(Boolean(shouldShowMax));
  }, [showSumAvgMetrics, metric]);

  return { showSum, showAvg, showMax };
};

/**
 * 获取展示 sum、avg 的指标配置
 *
 * @returns {{ sum: string[]; avg: string[]; max: string[] }} 包含展示 sum 和 avg 的 metrics 列表对象
 */
export const useShowSumAvgConfig = (): { sum: string[]; avg: string[]; max: string[] } => {
  // 获取公共配置
  const metricsDatasource: TMetricsDataSource = useQueryDefinition('datasource');

  // 获取需要展示 sum、avg 的指标
  const showSumAvgMetrics = useMemo(
    () => buildShowSumAvgMetrics(metricsDatasource),
    [metricsDatasource],
  );

  return showSumAvgMetrics;
};

export default useShowSumAvgInfo;
